import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { EnvironmentFilled, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import debounce from 'lodash.debounce';
import { ParsedUrlQuery } from 'querystring';
import { useMediaQuery } from 'react-responsive';
import styled, { DefaultTheme, withTheme } from 'styled-components';

import {
  FLAT_TYPE_GENERIC,
  SEARCH_BY_COUNTY_SUFFIX,
} from '../../common/consts';
import { BuyHouseOption } from '../../common/helpers/buyHouseOption.utils';
import { getFaqItems } from '../../common/helpers/faq.utils';
import {
  SearchOption,
  filterByText,
} from '../../common/helpers/searchOption.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import { generateQueryFromFilter } from '../../common/helpers/searchService.utils';
import {
  generateFaqStructuredData,
  generateSearchPageStructuredData,
} from '../../common/helpers/structuredData.utils';
import useCountyCityZone from '../../common/hooks/useCountyCityZone';
import useI18n from '../../common/hooks/useI18n';
import useSearchService, {
  SearchFilter,
} from '../../common/hooks/useSearchService';
import { IOffice } from '../../common/model/office.model';
import { IFlat } from '../../common/model/flat.model';
import { IMapArea } from '../../common/model/mapArea.model';
import { FlatOrderBy } from '../../common/model/enums/flatOrderBy.enum';

import {
  Description,
  Faq,
  Footer,
  Head,
  Header,
  ResultsBottomSection,
  ResultsSection,
  ResultsTopSection,
  SearchBar,
} from '../shared';
import {
  ClosedFlatsSection,
  FiltersModal,
  MiniFlatCard,
  NeighborsSection,
  SearchBreadcrumb,
  SearchMap,
  Title,
} from '.';
import { LinksGrid, LinksGridProvider } from '../linksGrid';

type Props = {
  htmlTitle: string;
  canonicalUrl: string;
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  flats: IFlat[];
  closedFlats?: IFlat[];
  results: IFlat[];
  searchOptions: SearchOption[];
  neighborsIds?: string[];
  metaRobotsNode?: ReactNode;
  searchBarText?: string;
  mapArea?: IMapArea;
  theme: DefaultTheme;
};

export const SHOW_EXTRA_FLATS_COUNT_LIMIT = 10;

const layoutId = 'comprarViviendasLayoutId';
const searchBarSectionId = 'comprarViviendasSearchBarSection';
const mapSectionId = 'comprarViviendasMapSection';
const transparentMddSectionId = 'comprarViviendasTransparentMddSection';
const scrollableSectionId = 'comprarViviendasScrollableSection';
const resultsInfoSectionId = 'comprarViviendasResultsInfoSection';
const headerOutOfScreenClass = 'header-out-of-screen';
const searchBarHeight = '48px';
const searchBarSectionPaddingTop = '16px';
const searchBarSectionPaddingBottom = '32px';
const transparentMddSectionBottomPadding = '112px';

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};
  min-height: ${(props) =>
    `calc(100vh - ${props.theme.headerHeight} - ${props.theme.footerHeight})`};
  min-height: ${(props) =>
    `calc(var(--vh-resizable, 1vh) * 100 - ${props.theme.headerHeight} - ${props.theme.footerHeight})`};

  &.no-results {
    #${scrollableSectionId} {
      margin-top: calc(
        ${searchBarHeight} + ${searchBarSectionPaddingTop} +
          ${searchBarSectionPaddingBottom}
      );
      border-radius: 0;

      @media ${(props) => props.theme.breakpoints.mdd} {
        margin-top: calc(${searchBarHeight} - ${searchBarSectionPaddingTop});
      }
    }

    #${resultsInfoSectionId} {
      margin: 0;
    }
  }

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
    min-height: ${(props) => `calc(100vh - ${props.theme.footerHeight})`};
    min-height: ${(props) =>
      `calc(var(--vh-resizable, 1vh) * 100 - ${props.theme.footerHeight})`};

    &.no-results {
      #${transparentMddSectionId} {
        height: 0;
      }
    }
  }
`;

const SearchBarSection = styled.div<{ $fullWidth: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(props) =>
    props.$fullWidth ? '100%' : props.theme.grid.getGridColumns(14, -1)};
  padding-top: ${searchBarSectionPaddingTop};
  padding-bottom: ${searchBarSectionPaddingBottom};
  padding-left: ${(props) =>
    props.theme.grid.getGridColumns(props.$fullWidth ? 2 : 1, 1)};
  padding-right: ${(props) =>
    props.theme.grid.getGridColumns(props.$fullWidth ? 2 : 1, 1)};
  z-index: 100;

  @media ${(props) => props.theme.breakpoints.mdd} {
    width: 100%;
    padding-bottom: 0;
  }

  &.${headerOutOfScreenClass} {
    position: fixed;
  }
`;

const MapSection = styled.div`
  position: fixed;
  top: ${(props) => props.theme.headerHeight};
  bottom: 0;
  left: ${(props) => props.theme.grid.getGridColumns(14, -1)};
  right: 0;
  z-index: 10;

  @media ${(props) => props.theme.breakpoints.mdd} {
    top: 0;
    bottom: unset;
    height: calc(100vh - 80px);
    height: calc(var(--vh-resizable, 1vh) * 100 - 80px);
    left: 0;
  }
`;

const MiniFlatCardsSection = styled.div`
  position: absolute;
  bottom: 48px;
  left: ${(props) => props.theme.grid.getGridColumns(4, 1)};
  right: ${(props) => props.theme.grid.getGridColumns(4, 1)};

  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    left: ${(props) => props.theme.grid.getGridColumns(1, 1)};
    right: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  }
`;

const ShowMapButton = styled(Button)`
  position: absolute;
  right: 24px;
  margin-top: -24px;

  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
`;

const TransparentMddSection = styled.div<{
  $mapVisible: boolean;
  $mapHasBeenVisible: boolean;
}>`
  position: relative;
  background-color: ${(props) =>
    props.$mapHasBeenVisible ? 'transparent' : 'white'};
  height: calc(
    100vh -${(props) => (props.$mapVisible ? transparentMddSectionBottomPadding : '90vh')}
  );
  height: calc(
    var(--vh-resizable, 1vh) * 100 -
      (
        ${(props) =>
          props.$mapVisible
            ? transparentMddSectionBottomPadding
            : 'var(--vh-resizable, 1vh) * 90'}
      )
  );
  pointer-events: none;
  transition: height 1s ease !important;

  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
`;

const ScrollableSection = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  padding-left: ${(props) =>
    props.theme.grid.getGridColumns(props.$fullWidth ? 2 : 1, 1)};
  padding-right: ${(props) =>
    props.theme.grid.getGridColumns(props.$fullWidth ? 2 : 1, 1)};
  margin-top: calc(
    ${searchBarHeight} + ${searchBarSectionPaddingTop} +
      ${searchBarSectionPaddingBottom}
  );
  min-height: ${(props) => `calc(100vh - ${props.theme.footerHeight})`};
  min-height: ${(props) =>
    `calc(var(--vh-resizable, 1vh) * 100 - ${props.theme.footerHeight})`};
  width: ${(props) =>
    props.$fullWidth ? '100%' : props.theme.grid.getGridColumns(14, -1)};
  background-color: white;
  z-index: 20;

  @media ${(props) => props.theme.breakpoints.md} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(4, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(4, 1)};
  }

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
    width: 100%;
    padding-top: 40px;
    border-top-left-radius: 40px;
    border-top-right-radius: 40px;
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }
`;

const FullWidthSection = styled.div`
  margin-left: ${(props) => props.theme.grid.getGridColumns(-2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(-2, 1)};
`;

const BreadcrumbContainer = styled.div`
  position: relative;
  margin-bottom: 24px;

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 16px;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(-20px);
      width: 40px;
      height: 4px;
      border-radius: 4px;
      background-color: rgba(32, 32, 32, 0.2);
    }
  }
`;

const InfoSection = styled.div`
  margin-top: 24px;
  padding-bottom: 16px;

  @media ${(props) => props.theme.breakpoints.xs} {
    margin-top: 16px;
  }
`;

const LoadMoreButtonRow = styled(Row)`
  margin-top: 24px;
`;

const LoadMoreButton = styled(Button)`
  width: 100%;
`;

const LinksGridContainer = styled.div`
  margin-top: 24px;
`;

const SearchLayout: React.FC<Props> = ({
  htmlTitle,
  canonicalUrl,
  offices,
  buyHouseOptions,
  flats,
  closedFlats = [],
  results: originalResults,
  searchOptions,
  neighborsIds = [],
  metaRobotsNode,
  searchBarText,
  mapArea,
  theme,
}) => {
  const router = useRouter();
  const i18n = useI18n();
  const isMdd = useMediaQuery({ query: theme.breakpoints.mdd });
  const searchService = useSearchService(flats, originalResults, mapArea);
  const [county, city, zone] = useCountyCityZone();

  const [mounted, setMounted] = useState(false);
  const [autoCompleteValue, setAutoCompleteValue] = useState(searchBarText);
  const [autoCompleteConfirmedValue, setAutoCompleteConfirmedValue] = useState(
    autoCompleteValue
  );
  const [mapVisible, setMapVisible] = useState(false);
  const [mapHasBeenVisible, setMapHasBeenVisible] = useState(!isMdd);
  const [filtersModalVisible, setFiltersModalVisible] = useState(false);
  const [focusedFlatIndex, setFocusedFlatIndex] = useState(0);
  const [highlightedPolygons, setHighlightedPolygons] = useState<
    google.maps.LatLngLiteral[][]
  >([]);

  const resultsSectionRef = useRef<HTMLElement | null>(null);
  const mapAreaRef = useRef(mapArea);

  const i18nQuery = useMemo(
    () => [county, city, zone].filter((v) => v).join('/'),
    [city, county, zone]
  );
  const metaTitle = useMemo(
    () =>
      i18n.t(`dynamic.searchPageUrls2.${i18nQuery}.metaTitle`) ||
      `${htmlTitle} | Inmobiliaria Núcleo`,
    [htmlTitle, i18n, i18nQuery]
  );
  const metaDescription = useMemo(
    () =>
      i18n.t(`dynamic.searchPageUrls2.${i18nQuery}.metaDescription`) ||
      i18n.t('search.metaDescription'),
    [i18n, i18nQuery]
  );

  const getCanonicalUrl = useCallback(
    () =>
      canonicalUrl.endsWith('/')
        ? canonicalUrl.substring(0, canonicalUrl.length - 1)
        : canonicalUrl,
    [canonicalUrl]
  );

  const structuredData = useMemo(
    () =>
      generateSearchPageStructuredData(
        metaTitle,
        metaDescription,
        getCanonicalUrl(),
        originalResults,
        i18n,
        router.query.flatType as string,
        [county, city, zone],
        mapArea
      ),
    [
      city,
      county,
      getCanonicalUrl,
      i18n,
      mapArea,
      metaDescription,
      metaTitle,
      originalResults,
      router.query.flatType,
      zone,
    ]
  );
  const faqStructuredData = useMemo(
    () =>
      generateFaqStructuredData(
        getFaqItems(i18n, 'searchPageUrls2', i18nQuery)
      ),
    [i18n, i18nQuery]
  );

  const setFocusedFlatIndexFromMap = useCallback(
    (index: number) => {
      setFocusedFlatIndex(index);
      if (
        !isMdd &&
        resultsSectionRef.current?.firstChild?.childNodes &&
        resultsSectionRef.current.firstChild.childNodes.length > index
      ) {
        const focusedEl = resultsSectionRef.current.firstChild.childNodes[
          index
        ] as Element;
        focusedEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
    [isMdd]
  );

  const searchInMap = useCallback(
    (bounds: google.maps.LatLngBounds) => {
      searchService.searchInMap(bounds);
    },
    [searchService]
  );

  const showMap = useCallback(() => {
    setMapVisible(!mapVisible);
    setMapHasBeenVisible(true);
  }, [mapVisible]);

  const handleScroll = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const headerHeight = +theme.headerHeight.replace('px', '');
    const footerHeight = +theme.footerHeight.replace('px', '');

    const layoutElement = document.getElementById(layoutId);
    const transparentMddSectionElement = document.getElementById(
      transparentMddSectionId
    );

    const searchBarSectionElement = document.getElementById(searchBarSectionId);
    if (searchBarSectionElement) {
      if (
        !searchBarSectionElement.className.includes(headerOutOfScreenClass) &&
        (isMdd || window.scrollY >= headerHeight)
      ) {
        searchBarSectionElement.className =
          searchBarSectionElement.className + ' ' + headerOutOfScreenClass;
      }
      if (
        searchBarSectionElement.className.includes(headerOutOfScreenClass) &&
        !isMdd &&
        window.scrollY < headerHeight
      ) {
        searchBarSectionElement.className = searchBarSectionElement.className.replace(
          headerOutOfScreenClass,
          ''
        );
      }
      if (transparentMddSectionElement) {
        searchBarSectionElement.style.setProperty(
          'background',
          `${
            isMdd && window.scrollY <= transparentMddSectionElement.clientHeight
              ? 'transparent'
              : 'linear-gradient( 180deg,rgba(255,255,255,0.95) 0%,rgba(255,255,255,0.75) 75%,rgba(255,255,255,0) 100% )'
          }`
        );
      }
    }

    const mapSectionElement = document.getElementById(mapSectionId);
    if (!isMdd && layoutElement && mapSectionElement) {
      const ammountOfHeaderVisible = headerHeight - window.scrollY;
      if (ammountOfHeaderVisible > 0) {
        mapSectionElement.style.setProperty(
          'top',
          `${ammountOfHeaderVisible}px`
        );
      }
      if (ammountOfHeaderVisible <= 0) {
        mapSectionElement.style.setProperty('top', '0');
      }

      const ammountOfFooterVisible =
        window.scrollY +
        window.innerHeight +
        footerHeight -
        layoutElement.clientHeight;
      if (ammountOfFooterVisible > 0) {
        mapSectionElement.style.setProperty(
          'bottom',
          `${ammountOfFooterVisible}px`
        );
      }
      if (ammountOfFooterVisible <= 0) {
        mapSectionElement.style.setProperty('bottom', '0');
      }
    }
  }, [isMdd, theme.footerHeight, theme.headerHeight]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleScroll = useCallback(
    debounce(handleScroll, 10, {
      leading: true,
      trailing: true,
    }),
    [handleScroll]
  );

  const setQueryParams = useCallback(
    (queryParams: ParsedUrlQuery) => {
      router.push(
        {
          pathname: window.location.pathname,
          query: { ...queryParams },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const onSearchBarSearch = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }
      setQueryParams(
        generateQueryFromFilter({
          ...searchService.getFilter,
          county: undefined,
          city: undefined,
          zone: undefined,
          q: value,
          mapArea: undefined,
          mapBounds: undefined,
        })
      );
      setAutoCompleteConfirmedValue(value);
    },
    [searchService, setQueryParams]
  );

  const onSearchBarSelect = useCallback(
    (option: SearchOption) => {
      setQueryParams(
        generateQueryFromFilter({
          ...searchService.getFilter(),
          county: option.county
            ? slugifySearchQuery(option.county) + SEARCH_BY_COUNTY_SUFFIX
            : undefined,
          city: option.city ? slugifySearchQuery(option.city) : undefined,
          zone: option.zone ? slugifySearchQuery(option.zone) : undefined,
          q: undefined,
          mapArea: undefined,
          mapBounds: undefined,
        })
      );

      setAutoCompleteValue(option.text);
      setAutoCompleteConfirmedValue(option.text);
    },
    [searchService, setQueryParams]
  );

  const onFilterChange = useCallback(
    (filter: SearchFilter) => {
      const currentFilter = searchService.getFilter();
      const queryParams = generateQueryFromFilter({
        county:
          currentFilter.county === router.query.county
            ? undefined
            : currentFilter.county,
        city:
          currentFilter.city === router.query.city
            ? undefined
            : currentFilter.city,
        zone:
          currentFilter.zone === router.query.zone
            ? undefined
            : currentFilter.zone,
        q: currentFilter.q,
        ...filter,
      });
      setQueryParams(queryParams);

      setFiltersModalVisible(false);
    },
    [
      router.query.city,
      router.query.county,
      router.query.zone,
      searchService,
      setQueryParams,
    ]
  );

  const patchFilter = useCallback(
    (filter) => onFilterChange({ ...searchService.getFilter(), ...filter }),
    [onFilterChange, searchService]
  );

  useEffect(() => {
    setMounted(true);

    setHighlightedPolygons(mapAreaRef.current?.polygons ?? []);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', () => debouncedHandleScroll());

    setTimeout(() => debouncedHandleScroll());

    return () =>
      window.removeEventListener('scroll', () => debouncedHandleScroll());
  }, [debouncedHandleScroll]);

  useEffect(() => {
    setFocusedFlatIndex(0);

    if (router.query.q) {
      setAutoCompleteValue(router.query.q as string);
    }
  }, [router.query]);

  // Cannot use useCallback here
  const hasFilters = (): boolean => {
    if (!mounted || typeof window === 'undefined') {
      return false;
    }

    if (router.query.flatType !== FLAT_TYPE_GENERIC) {
      return true;
    }

    const searchParams = new URLSearchParams(window.location.search);
    return (
      Array.from(searchParams.keys()).filter(
        (k) =>
          !['county', 'city', 'zone', 'q', 'mapArea', 'mapBounds'].includes(k)
      ).length > 0
    );
  };

  return (
    <Layout as="div" id={layoutId}>
      <Head
        title={metaTitle}
        description={metaDescription}
        canonicalUrl={getCanonicalUrl()}
        metaRobotsNode={metaRobotsNode}
      >
        {structuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        ) : null}
        {faqStructuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqStructuredData),
            }}
          />
        ) : null}
      </Head>

      {!isMdd && <Header offices={offices} buyHouseOptions={buyHouseOptions} />}

      <Content
        as="main"
        className={searchService.getResultsCount() === 0 ? 'no-results' : ''}
      >
        <SearchBarSection
          as="div"
          id={searchBarSectionId}
          $fullWidth={searchService.getResultsCount() === 0}
        >
          <SearchBar
            height={searchBarHeight}
            value={autoCompleteValue}
            options={filterByText(searchOptions, autoCompleteValue)}
            onValueChange={setAutoCompleteValue}
            onSearch={onSearchBarSearch}
            onSelect={onSearchBarSelect}
            onFiltersButtonClick={() => {
              setFiltersModalVisible(true);
            }}
            applyFilter={patchFilter}
            hasFilters={hasFilters()}
            backButton={isMdd}
            inputPadding={isMdd ? '0' : undefined}
          />
        </SearchBarSection>

        <MapSection as="div" id={mapSectionId}>
          {typeof google !== 'undefined' &&
            typeof google.maps.Map !== 'undefined' &&
            searchService.getResults().length > 0 &&
            mapHasBeenVisible && (
              <SearchMap
                flats={searchService.getResults()}
                focusedFlatIndex={focusedFlatIndex}
                highlightedPolygons={highlightedPolygons}
                onMarkerClick={setFocusedFlatIndexFromMap}
                searchInMap={searchInMap}
              />
            )}
          {searchService.getResultsCount() > 0 && (
            <MiniFlatCardsSection>
              <MiniFlatCard
                flat={searchService.getResults()[focusedFlatIndex]}
              />
            </MiniFlatCardsSection>
          )}
        </MapSection>

        <TransparentMddSection
          as="div"
          id={transparentMddSectionId}
          $mapVisible={mapVisible}
          $mapHasBeenVisible={mapHasBeenVisible}
        />

        <ScrollableSection
          as="div"
          id={scrollableSectionId}
          $fullWidth={searchService.getResultsCount() === 0}
        >
          <ShowMapButton
            type="primary"
            icon={
              mapVisible ? <UnorderedListOutlined /> : <EnvironmentFilled />
            }
            onClick={showMap}
          >
            {i18n.t(`search.actions.${mapVisible ? 'hideMap' : 'showMap'}`)}
          </ShowMapButton>

          <BreadcrumbContainer>
            <SearchBreadcrumb searchOptions={searchOptions} mapArea={mapArea} />
          </BreadcrumbContainer>

          {searchService.getResultsCount() > 0 && (
            <>
              <Title
                searchService={searchService}
                autoCompleteConfirmedValue={autoCompleteConfirmedValue}
                searchOptions={searchOptions}
                i18nQuery={i18nQuery}
              />
              <ResultsTopSection
                resultsCount={searchService.getResultsCount()}
                orderBy={searchService.getOrderBy()}
                onOrderByChange={(orderBy) =>
                  searchService.setOrderBy(orderBy as FlatOrderBy)
                }
              />
            </>
          )}

          <ResultsSection
            flats={searchService.getResults()}
            offices={offices}
            focusedFlatIndex={focusedFlatIndex}
            focusedCardBackgroundColor="#f8f8f8"
            cardBackgroundColor="white"
            parentRef={resultsSectionRef}
            onFlatHover={(_flat, index) => {
              setFocusedFlatIndex(index);
            }}
          />
          <InfoSection as="div" id={resultsInfoSectionId}>
            <ResultsBottomSection
              pageSize={searchService.getPageSize()}
              resultsCount={searchService.getResultsCount()}
            />

            {searchService.getResultsCount() > 0 &&
              searchService.getPageSize() < searchService.getResultsCount() && (
                <LoadMoreButtonRow justify="center">
                  <Col
                    xs={{ span: 20 }}
                    sm={{ span: 16 }}
                    md={{ span: 12 }}
                    lg={{ span: 10 }}
                    xl={{ span: 8 }}
                  >
                    <LoadMoreButton
                      type="primary"
                      onClick={() => {
                        searchService.incrementPageSize();
                        setTimeout(() => debouncedHandleScroll());
                      }}
                    >
                      <span>{i18n.t('search.actions.loadMore')}</span>
                    </LoadMoreButton>
                  </Col>
                </LoadMoreButtonRow>
              )}
          </InfoSection>

          {i18n.t(`dynamic.searchPageUrls2.${i18nQuery}.descriptionSeo`) && (
            <InfoSection>
              <Description
                htmlText={i18n.t(
                  `dynamic.searchPageUrls2.${i18nQuery}.descriptionSeo`
                )}
              />
            </InfoSection>
          )}

          {originalResults.length < SHOW_EXTRA_FLATS_COUNT_LIMIT &&
            neighborsIds.length > 0 && (
              <FullWidthSection>
                <NeighborsSection
                  flats={flats}
                  neighborsIds={neighborsIds}
                  offices={offices}
                  hasSomeResults={originalResults.length > 0}
                />
              </FullWidthSection>
            )}

          {i18n.t(`dynamic.searchPageUrls2.${i18nQuery}.faqTitle_1`) && (
            <InfoSection>
              <Faq
                spreadsheetName="searchPageUrls2"
                query={i18nQuery}
                theme={theme}
              />
            </InfoSection>
          )}

          {originalResults.length > 0 &&
            originalResults.length < SHOW_EXTRA_FLATS_COUNT_LIMIT && (
              <FullWidthSection>
                <ClosedFlatsSection
                  closedFlats={closedFlats}
                  offices={offices}
                />
              </FullWidthSection>
            )}

          <LinksGridProvider mode="search" flats={flats} offices={offices}>
            <LinksGridContainer>
              <LinksGrid />
            </LinksGridContainer>
          </LinksGridProvider>
        </ScrollableSection>

        <FiltersModal
          visible={filtersModalVisible}
          onOk={onFilterChange}
          onCancel={() => {
            setFiltersModalVisible(false);
          }}
        />
      </Content>

      <Footer />
    </Layout>
  );
};

export default withTheme<typeof SearchLayout>(SearchLayout);
